import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import {
  getMedicationReminders,
  getMedicationAdherence,
  logMedication,
} from '../services/health-intelligence-service';
import type {
  MedicationReminder,
  MedicationAdherence,
  MedicationLogEntry,
} from '../types/health-intelligence-types';

export const medicationReminderKeys = {
  all: ['medicationReminders'] as const,
  today: (userId: string | undefined) =>
    [...medicationReminderKeys.all, 'today', userId] as const,
  adherence: (userId: string | undefined) =>
    [...medicationReminderKeys.all, 'adherence', userId] as const,
};

/**
 * Hook to manage medication reminders and adherence tracking
 *
 * @returns Query results with reminders, adherence data, and logging functionality
 */
export const useMedicationReminders = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const remindersQuery = useQuery<MedicationReminder[]>({
    queryKey: medicationReminderKeys.today(userId),
    queryFn: () => getMedicationReminders(userId!),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes - changes often
  });

  const adherenceQuery = useQuery<MedicationAdherence[]>({
    queryKey: medicationReminderKeys.adherence(userId),
    queryFn: () => getMedicationAdherence(userId!),
    enabled: !!userId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  const logMutation = useMutation({
    mutationFn: (entry: Omit<MedicationLogEntry, 'id'>) => logMedication(entry),
    onSuccess: () => {
      // Invalidate both queries to refresh data
      queryClient.invalidateQueries({
        queryKey: medicationReminderKeys.today(userId),
      });
      queryClient.invalidateQueries({
        queryKey: medicationReminderKeys.adherence(userId),
      });
    },
  });

  const reminders = remindersQuery.data ?? [];
  const adherence = adherenceQuery.data ?? [];

  // Computed values
  const overdueCount = reminders.filter((r) => r.isOverdue).length;
  const nextReminder = reminders
    .filter((r) => r.status === 'pending')
    .sort(
      (a, b) =>
        new Date(a.scheduledTime).getTime() -
        new Date(b.scheduledTime).getTime()
    )[0];

  const isLoading = remindersQuery.isLoading || adherenceQuery.isLoading;

  return {
    reminders,
    adherence,
    isLoading,
    logMedication: logMutation.mutate,
    isLogging: logMutation.isPending,
    overdueCount,
    nextReminder,
  };
};
